/**
 * rateEntryPool.js — every player /rate's roster ladder can offer.
 *
 * 🔴 THE BOARD IS NOT THE WHOLE POOL, AND THAT WAS COSTING PEOPLE THEIR REPORT.
 * The chunk-b6d60a3 audit found 25 players the projection set carries and
 * UNIQUE_PLAYERS does not: Kirk Cousins, Cole Kmet, Mo Alie-Cox, Dawson Knox,
 * and 21 more. The autocomplete drew from UNIQUE_PLAYERS alone, so a roster
 * with any of them could not be entered AT ALL. Not graded badly, not shown
 * with a dash - unenterable, on a form whose whole job is to accept a roster.
 * That is a worse failure than any wrong number on the report.
 *
 * 🔴 DERIVED, NEVER A HAND-MAINTAINED LIST. The merge is computed at module
 * load from the same normName + position-group index the audit test uses, so
 * the next projections regeneration updates it by itself. A typed list of 25
 * names would be right today, wrong after the next `npm run gen-projections`,
 * and wrong silently - which is exactly how these 25 went unnoticed.
 *
 * WHAT AN ADDED PLAYER LOOKS LIKE. Name and position group off the projection
 * row, team off the DEFAULT_PROJECTIONS key. No sleeper_id, because there is
 * nowhere to get one: PlayerAvatar already falls back to an initials circle
 * when the id is absent, so the row is finished without it.
 *
 * ⚠️ THE ADDITIONS GO LAST. UNIQUE_PLAYERS is in board order and the
 * autocomplete takes the first handful of matches, so appending keeps every
 * existing search result exactly where it was. The added names are the ones
 * with no board entry to compete with, so they surface as soon as the query
 * reaches them.
 *
 * 🔴 KICKERS COME IN THE SAME WAY, FROM THE SAME RULE (Aug 30 2026). The
 * board carries 18 of the 32 starting kickers, and the ladder gained a K row
 * the moment K became projectable, so the other 14 were players a league could
 * roster and the form could not accept - the identical failure the 25 skill
 * players were. They are derived from KD_PROJECTIONS_2026 rather than listed,
 * so refreshing that table refreshes the pool. All 32 defenses are already on
 * the board and the merge finds nothing to add for DEF, which is the correct
 * outcome rather than a special case.
 */

import { DEFAULT_PROJECTIONS } from "../data/defaultProjections2026.js";
import { KD_PROJECTIONS_2026 } from "../data/kdProjections2026.js";
import { UNIQUE_PLAYERS } from "../data/players.js";
import { normName } from "./playerNameNorm.js";
import { KD_POSITIONS, PROJECTED_POSITIONS, projGroup } from "./rateProjection.js";

/** The stat-line half of the projection set: what DEFAULT_PROJECTIONS carries. */
const SKILL_GROUPS = new Set(PROJECTED_POSITIONS.filter((g) => !KD_POSITIONS.includes(g)));

/** The index both this module and the audit test key on: normName + group. */
export function poolKey(name, pos) {
  const group = projGroup(pos);
  return group ? `${normName(name)}|${group}` : null;
}

/**
 * Projected players with no UNIQUE_PLAYERS entry, in the shape the ladder and
 * PlayerAvatar read. Deterministic: rows are visited in file order, and a
 * duplicate key keeps the first, so two regenerations of the same data produce
 * the same pool.
 */
function missingFromBoard() {
  const onBoard = new Set();
  for (const p of UNIQUE_PLAYERS) {
    const k = poolKey(p.name, p.pos);
    if (k) onBoard.add(k);
  }

  const added = [];
  const seen = new Set();
  for (const [team, roster] of Object.entries(DEFAULT_PROJECTIONS)) {
    for (const row of roster) {
      const group = projGroup(row.pos);
      if (!group || !SKILL_GROUPS.has(group)) continue;
      const k = poolKey(row.player, row.pos);
      if (!k || onBoard.has(k) || seen.has(k)) continue;
      seen.add(k);
      added.push({
        name: row.player,
        // The GROUP, not the raw projection position: the ladder tests slot
        // eligibility against this value, and FLEX_ELIGIBLE speaks groups.
        pos: group,
        team,
        sleeper_id: null,
        // Marks where the row came from. Nothing branches on it today; it is
        // here so a future reader of a pool entry can tell.
        fromProjections: true,
      });
    }
  }
  // The kickers and defenses the report can project, on the same rule: in the
  // table, not on the board. `team` is the table's own key, which is the
  // abbreviation players.js speaks (JAC, not JAX).
  for (const [team, entry] of Object.entries(KD_PROJECTIONS_2026)) {
    for (const [pos, row] of [["DEF", entry.def], ["K", entry.k]]) {
      const k = poolKey(row.name, pos);
      if (!k || onBoard.has(k) || seen.has(k)) continue;
      seen.add(k);
      added.push({ name: row.name, pos, team, sleeper_id: null, fromProjections: true });
    }
  }
  return added;
}

/** The additions alone, exported so a test can assert the merge did something. */
export const PROJECTED_ONLY_PLAYERS = missingFromBoard();

/** The board, then everyone the board is missing. Built once, at module load. */
export const ENTRY_POOL = [...UNIQUE_PLAYERS, ...PROJECTED_ONLY_PLAYERS];
